import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { PoolType } from "@/hooks/useProductPools";

interface Props {
  open: boolean;
  poolType: PoolType;
  currentBalance: number;
  wouldBeBalance: number;
  delta: number;
  isConfirming: boolean;
  onCancel: () => void;
  onProceed: () => void;
}

const NegativeBalanceConfirmDialog: React.FC<Props> = ({
  open,
  poolType,
  currentBalance,
  wouldBeBalance,
  delta,
  isConfirming,
  onCancel,
  onProceed,
}) => {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Negative balance</DialogTitle>
          <DialogDescription>
            This adjustment will take the <strong>{poolType}</strong> pool below zero.
            Confirm to proceed — the event will still be recorded as an immutable ledger entry.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/30 p-4 text-sm">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-xs uppercase text-muted-foreground">Current</div>
              <div className="text-lg font-semibold">{currentBalance.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground">Delta</div>
              <div className={`text-lg font-semibold ${delta < 0 ? "text-red-600" : "text-green-600"}`}>
                {delta > 0 ? "+" : ""}{delta.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground">Would-be</div>
              <div className="text-lg font-semibold text-red-600">{wouldBeBalance.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isConfirming}>
            Cancel
          </Button>
          <Button onClick={onProceed} disabled={isConfirming}>
            {isConfirming ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Recording…
              </>
            ) : (
              "Proceed (allow negative)"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NegativeBalanceConfirmDialog;
